import * as React from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

// Common country codes
const COUNTRY_CODES = [
  { code: "US", dial: "+1", flag: "🇺🇸", name: "United States" },
  { code: "CA", dial: "+1", flag: "🇨🇦", name: "Canada" },
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "AU", dial: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "France" },
  { code: "IT", dial: "+39", flag: "🇮🇹", name: "Italy" },
  { code: "ES", dial: "+34", flag: "🇪🇸", name: "Spain" },
  { code: "MX", dial: "+52", flag: "🇲🇽", name: "Mexico" },
  { code: "BR", dial: "+55", flag: "🇧🇷", name: "Brazil" },
  { code: "AR", dial: "+54", flag: "🇦🇷", name: "Argentina" },
  { code: "IN", dial: "+91", flag: "🇮🇳", name: "India" },
  { code: "CN", dial: "+86", flag: "🇨🇳", name: "China" },
  { code: "JP", dial: "+81", flag: "🇯🇵", name: "Japan" },
  { code: "KR", dial: "+82", flag: "🇰🇷", name: "South Korea" },
  { code: "RU", dial: "+7", flag: "🇷🇺", name: "Russia" },
  { code: "ZA", dial: "+27", flag: "🇿🇦", name: "South Africa" },
  { code: "NG", dial: "+234", flag: "🇳🇬", name: "Nigeria" },
  { code: "EG", dial: "+20", flag: "🇪🇬", name: "Egypt" },
];

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  defaultCountry?: string;
  disabled?: boolean;
}

export const PhoneInput = React.forwardRef<HTMLDivElement, PhoneInputProps>(
  ({ value = "", onChange, placeholder = "Phone number", className, defaultCountry = "US", disabled }, ref) => {
    // Parse the value to extract country code and number
    const parsePhoneNumber = (fullNumber: string) => {
      if (!fullNumber) return { countryCode: defaultCountry, number: "" };
      
      // Find matching country by dial code
      const country = COUNTRY_CODES.find(c => fullNumber.startsWith(c.dial));
      if (country) {
        return {
          countryCode: country.code,
          number: fullNumber.slice(country.dial.length).trim()
        };
      }
      
      // Default to US if no match
      return { countryCode: defaultCountry, number: fullNumber.replace(/^\+/, "") };
    };

    const { countryCode, number } = parsePhoneNumber(value);
    const [selectedCountry, setSelectedCountry] = React.useState(countryCode);
    const [phoneNumber, setPhoneNumber] = React.useState(number);

    // Get current country details
    const currentCountry = COUNTRY_CODES.find(c => c.code === selectedCountry) || COUNTRY_CODES[0];

    // Update parent value when country or number changes
    React.useEffect(() => {
      if (onChange) {
        const fullNumber = phoneNumber ? `${currentCountry.dial}${phoneNumber}` : "";
        onChange(fullNumber);
      }
    }, [selectedCountry, phoneNumber, currentCountry.dial, onChange]);

    // Format phone number based on country (simplified US formatting for now)
    const formatPhoneNumber = (input: string, countryCode: string): string => {
      const digits = input.replace(/\D/g, "");
      
      if (countryCode === "US" || countryCode === "CA") {
        // Format as (XXX) XXX-XXXX
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
      
      // For other countries, just show the number
      return digits;
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const digitsOnly = input.replace(/\D/g, "");
      setPhoneNumber(digitsOnly);
    };

    return (
      <div ref={ref} className={cn("flex", className)}>
        <Select
          value={selectedCountry}
          onValueChange={setSelectedCountry}
          disabled={disabled}
        >
          <SelectTrigger className="w-[110px] rounded-r-none border-r-0 focus:z-10">
            <SelectValue>
              <div className="flex items-center gap-2">
                <span>{currentCountry.flag}</span>
                <span className="text-xs">{currentCountry.dial}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                <div className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span>{country.dial}</span>
                  <span className="text-muted-foreground">{country.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="tel"
          value={formatPhoneNumber(phoneNumber, selectedCountry)}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className="rounded-l-none"
          disabled={disabled}
        />
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export type PhoneInputValue = string;